import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { PrintoutSchedule } from "../../../utils/models";
import { buildAnchorShiftTimeAliasMap } from "../layout/buildShiftTimeAlias";
import { isMergeSimilarActive } from "../predicates/isMergeSimilarActive";
import type { HorizontalLayout, PrintoutColumn, PrintoutColumnType, RowNoteFlags, ScheduleRow } from "../types";
import { populateCell } from "./cellPopulator";
import { THIN_BORDER } from "./cellStyle";
import { composeMergeGroupKey, sortRowsByMergeColumns } from "./mergeColumnSort";

interface RenderPivotSectionRowsArgs {
	worksheet: Worksheet;
	rows: ScheduleRow[];
	currentRow: number;
	columnGroupingBy: PrintoutColumnType;
	layout: HorizontalLayout;
	enabledColumns: PrintoutColumn[];
	rowGroupingBy: PrintoutColumnType[];
	hospitalData: PrintoutSchedule;
	department: Department;
	timezone: string;
	jobManagementEnabled: boolean;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	mergeSimilarShiftTimes: boolean | undefined;
	noteFlags: RowNoteFlags | undefined;
	appendRoleToName: boolean;
}

/**
 * Emit data rows for one section in horizontal-pivot mode (mapping §8.2).
 *
 * Flow:
 * 1. Build the anchor-key alias map (mapping §8.2 Case B) — only if `mergeSimilarShiftTimes` is
 *    actually firing for this configuration AND shift time is an anchor column
 * 2. Pre-sort the section's rows by anchor columns (using the shared `compareValues` chain)
 * 3. Bucket rows by (anchor key, horizontal value) so each anchor group can render side-by-side
 * 4. For each anchor key (in encounter order), emit rows up to the largest staff count across bands;
 *    anchor cells only get populated on the first row of each group
 * 5. Post-pass: hierarchically merge anchor columns across consecutive groups sharing the same prefix
 *
 * Returns the next row number after the section's data.
 */
export const renderPivotSectionRows = (args: RenderPivotSectionRowsArgs): number => {
	const {
		worksheet,
		rows,
		currentRow: initialRow,
		columnGroupingBy,
		layout,
		enabledColumns,
		rowGroupingBy,
		hospitalData,
		department,
		timezone,
		jobManagementEnabled,
		sortBy,
		customStringOrderMap,
		mergeSimilarShiftTimes,
		noteFlags,
		appendRoleToName,
	} = args;
	const { anchorColumns, subColumns, horizontalValues, horizontalKeysByRow } = layout;

	let currentRow = initialRow;

	const anchorColTypes: PrintoutColumnType[] = anchorColumns.map((c) => c.type);
	const rawAnchorKey = (row: ScheduleRow): string => composeMergeGroupKey(row, anchorColTypes, jobManagementEnabled);

	// Anchor-key alias map (mapping §8.2 Case B) — only built if mergeSimilarShiftTimes is active AND
	// shift time is one of the anchor columns
	const shiftTimeAnchorIdx = anchorColumns.findIndex((col) => col.type === "shiftTime");
	const mergeActive = isMergeSimilarActive({ mergeSimilarShiftTimes, columnGroupingBy, rowGroupingBy });
	const anchorAliasMap =
		mergeActive && shiftTimeAnchorIdx >= 0
			? buildAnchorShiftTimeAliasMap({
					rows,
					rawAnchorKey,
					shiftTimeAnchorIdx,
					otherAnchorPart: (row, idx) => composeMergeGroupKey(row, [anchorColumns[idx].type], jobManagementEnabled),
				})
			: new Map<string, string>();

	const getAnchorKey = (row: ScheduleRow): string => anchorAliasMap.get(rawAnchorKey(row)) ?? rawAnchorKey(row);

	// Pre-sort rows by anchor columns. Mutates `rows` in place — caller passed a fresh array.
	const sortedRows = sortRowsByMergeColumns({
		rows,
		mergeCols: anchorColTypes,
		enabledColumns,
		sortBy,
		customStringOrderMap,
		jobManagementEnabled,
	});

	// Bucket by (anchor key, horizontal value), preserving anchor-key encounter order
	const anchorKeyOrder: string[] = [];
	const anchorGroupMap = new Map<string, Map<string, ScheduleRow[]>>();
	const groupMeta: { anchorKey: string; startRow: number; endRow: number }[] = [];

	for (const row of sortedRows) {
		const anchorKey = getAnchorKey(row);
		let hGroup = anchorGroupMap.get(anchorKey);
		if (!hGroup) {
			hGroup = new Map();
			anchorGroupMap.set(anchorKey, hGroup);
			anchorKeyOrder.push(anchorKey);
		}
		const horizontalKeys = horizontalKeysByRow.get(row);
		if (!horizontalKeys) {
			throw new Error(`Missing horizontal pivot value for ${columnGroupingBy}`);
		}
		for (const horizontalKey of horizontalKeys) {
			const bucket = hGroup.get(horizontalKey);
			if (bucket) bucket.push(row);
			else hGroup.set(horizontalKey, [row]);
		}
	}

	// Emit rows. Prefer a row whose raw key matches the anchor key (i.e. from the largest group)
	// so anchor cells reflect the winning variant when `mergeSimilarShiftTimes` aliases multiple
	// keys together.
	for (const anchorKey of anchorKeyOrder) {
		const hGroup = anchorGroupMap.get(anchorKey);
		if (!hGroup) continue;
		const maxRows = Math.max(...horizontalValues.map((hv) => hGroup.get(hv)?.length ?? 0), 1);
		const groupStartRow = currentRow;

		const representativeRow = sortedRows.find((r) => rawAnchorKey(r) === anchorKey) ?? sortedRows.find((r) => getAnchorKey(r) === anchorKey);

		for (let i = 0; i < maxRows; i++) {
			let colOffset = 1;

			// Anchor cells: only on the first row of the group
			for (const col of anchorColumns) {
				const cell = worksheet.getCell(currentRow, colOffset);
				if (i === 0) {
					populateCell({ cell, col, row: representativeRow, hospitalData, timezone, department, jobManagementEnabled, noteFlags, appendRoleToName });
				} else {
					cell.value = "";
				}
				cell.border = THIN_BORDER;
				colOffset += 1;
			}

			// Sub-columns under each band
			for (let hIdx = 0; hIdx < horizontalValues.length; hIdx++) {
				const hVal = horizontalValues[hIdx];
				const staffRows = hGroup.get(hVal) ?? [];
				const staffRow = staffRows[i];
				const isLastBand = hIdx === horizontalValues.length - 1;
				for (let subIdx = 0; subIdx < subColumns.length; subIdx++) {
					const col = subColumns[subIdx];
					const cell = worksheet.getCell(currentRow, colOffset);
					populateCell({ cell, col, row: staffRow, hospitalData, timezone, department, jobManagementEnabled, noteFlags, appendRoleToName });
					const isLastInBand = subIdx === subColumns.length - 1;
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: isLastInBand && !isLastBand ? "medium" : "thin" },
					};
					colOffset += 1;
				}
			}

			currentRow += 1;
		}

		groupMeta.push({ anchorKey, startRow: groupStartRow, endRow: currentRow - 1 });
	}

	// Post-pass: hierarchically merge anchor columns across consecutive groups sharing the same prefix.
	for (let colIdx = 0; colIdx < anchorColumns.length; colIdx++) {
		const excelCol = colIdx + 1;
		const prefix = (meta: { anchorKey: string }) =>
			meta.anchorKey
				.split("|")
				.slice(0, colIdx + 1)
				.join("|");
		let mergeStartIdx = 0;

		for (let i = 1; i <= groupMeta.length; i++) {
			const samePrefix = i < groupMeta.length && prefix(groupMeta[i]) === prefix(groupMeta[mergeStartIdx]);
			if (!samePrefix) {
				const startRow = groupMeta[mergeStartIdx].startRow;
				const endRow = groupMeta[i - 1].endRow;
				if (endRow > startRow) {
					worksheet.mergeCells(startRow, excelCol, endRow, excelCol);
					worksheet.getCell(startRow, excelCol).alignment = { vertical: "middle", horizontal: "left" };
					for (let r = startRow; r <= endRow; r++) {
						worksheet.getCell(r, excelCol).border = {
							top: r === startRow ? { style: "thin" } : undefined,
							bottom: r === endRow ? { style: "thin" } : undefined,
							left: { style: "thin" },
							right: { style: "thin" },
						};
					}
				}
				mergeStartIdx = i;
			}
		}
	}

	return currentRow;
};
